import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const OfflinePaymentDetails = () => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filter states
  const [filters, setFilters] = useState({
    labType: '',
    userName: '',
    transactionType: '0', // 0 for Pending, 1 for Approved
    orderId: '',
    dateFrom: null,
    dateTo: null
  });

  // Data states
  const [paymentData, setPaymentData] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Lab types
  const labTypes = [
    { value: '', label: '--Select--' },
    { value: '1', label: 'Testing' },
    { value: '3', label: 'Medical' },
    { value: '2', label: 'Calibration' },
    { value: '4', label: 'PTP' },
    { value: '5', label: 'RMP' }
  ];

  // Fetch payment types on component mount
  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchOfflinePaymentData();
  }, [filters, currentPage]);

  const fetchPaymentTypes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/accounts/payment-types`);
      const result = await response.json();
      
      if (result.success) {
        setPaymentTypes([
          { id: '', Description: '--Select Transaction Type--' },
          ...result.data
        ]);
      }
    } catch (error) {
      console.error('Error fetching payment types:', error);
    }
  };

  const fetchOfflinePaymentData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10'
      });

      // Add filters only if they have values
      if (filters.labType) {
        params.append('labType', filters.labType);
      }
      
      if (filters.userName) {
        params.append('userName', filters.userName);
      }
      
      if (filters.transactionType !== '') {
        params.append('status', filters.transactionType);
      }
      
      if (filters.orderId) {
        params.append('orderId', filters.orderId);
      }
      
      if (filters.dateFrom) {
        params.append('dateFrom', format(filters.dateFrom, 'yyyy-MM-dd'));
      }
      
      if (filters.dateTo) {
        params.append('dateTo', format(filters.dateTo, 'yyyy-MM-dd'));
      }

      console.log('API Request params:', params.toString());
      console.log('Current filters:', filters);
      console.log('Current page:', currentPage);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/accounts/offline-payment-details?${params}`
      );
      
      console.log('API Response status:', response.status);
      const result = await response.json();
      console.log('API Response:', result);
      
      if (result.success) {
        setPaymentData(result.data || []);
        setTotalPages(result.totalPages || 1);
        setTotalRecords(result.totalRecords || 0);
      } else {
        setError(result.message || 'Failed to fetch offline payment data');
      }
    } catch (error) {
      setError('Error fetching offline payment data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    console.log('Page changed to:', newPage);
    setCurrentPage(newPage);
  };

  const handleFilterChange = (field, value) => {
    console.log('Filter changed:', field, '=', value);
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleResetFilters = () => {
    setFilters({
      labType: '',
      userName: '',
      transactionType: '0', // Reset to Pending
      orderId: '',
      dateFrom: null,
      dateTo: null
    });
    setCurrentPage(1);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();

      // Add filters only if they have values
      if (filters.labType) {
        params.append('labType', filters.labType);
      }
      
      if (filters.userName) {
        params.append('userName', filters.userName);
      }
      
      if (filters.transactionType !== '') {
        params.append('status', filters.transactionType);
      }
      
      if (filters.orderId) {
        params.append('orderId', filters.orderId);
      }
      
      if (filters.dateFrom) {
        params.append('dateFrom', format(filters.dateFrom, 'yyyy-MM-dd'));
      }
      
      if (filters.dateTo) {
        params.append('dateTo', format(filters.dateTo, 'yyyy-MM-dd'));
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/accounts/offline-payment-details/export?${params}`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `offline-payment-details-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setSuccess('Export completed successfully!');
      } else {
        setError('Failed to export data');
      }
    } catch (error) {
      setError('Error exporting data: ' + error.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleApprove = (appId, paymentTypeId, loginName) => {
    const approveUrl = `/main/accounts/offline-payment-form?appid=${btoa(appId)}&payType=${btoa(paymentTypeId)}&app=${btoa(loginName)}`;
    window.open(approveUrl);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#00024c' }}>
          Offline Payment Details
        </Typography>
        
        {/* Success/Error Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Filters Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterIcon sx={{ mr: 1, color: '#1976d2' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Filters
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleResetFilters}
                  size="small"
                >
                  Reset Filter
                </Button>
                <Button
                  variant="contained"
                  startIcon={exportLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={handleExport}
                  disabled={exportLoading}
                  size="small"
                >
                  Download Excel
                </Button>
              </Box>
            </Box>

            {/* Transaction Status Filter */}
            <Box sx={{ mb: 2 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  Transaction Status
                </FormLabel>
                <RadioGroup
                  row
                  value={filters.transactionType}
                  onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                >
                  <FormControlLabel value="1" control={<Radio size="small" />} label="Approved" />
                  <FormControlLabel value="0" control={<Radio size="small" />} label="Pending" />
                </RadioGroup>
              </FormControl>
            </Box>

            <Grid container spacing={2}>
              {/* Lab Type */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Lab Type</InputLabel>
                  <Select
                    value={filters.labType}
                    label="Lab Type"
                    onChange={(e) => handleFilterChange('labType', e.target.value)}
                  >
                    {labTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Username */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="User Name"
                  placeholder="Enter UserName"
                  value={filters.userName}
                  onChange={(e) => handleFilterChange('userName', e.target.value)}
                />
              </Grid>

              {/* Date From */}
              {/* <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Date From"
                  value={filters.dateFrom}
                  onChange={(date) => handleFilterChange('dateFrom', date)}
                  maxDate={new Date()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      required: filters.dateTo ? true : false
                    }
                  }}
                />
              </Grid> */}

              {/* Date To */}
              {/* <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="Date To"
                  value={filters.dateTo}
                  onChange={(date) => handleFilterChange('dateTo', date)}
                  maxDate={new Date()}
                  minDate={filters.dateFrom}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      required: filters.dateFrom ? true : false
                    }
                  }}
                />
              </Grid> */}

              {/* Search Button */}
              <Grid item xs={12} sm={6} md={2} sx={{ display: 'none'}}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => {
                    console.log('Manual search clicked with filters:', filters);
                    setCurrentPage(1);
                    fetchOfflinePaymentData();
                  }}
                  sx={{ height: '40px' }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {/* Pagination Info */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {paymentData.length > 0 ? ((currentPage - 1) * 10) + 1 : 0} - {Math.min(currentPage * 10, totalRecords)} of {totalRecords} records
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          </Box>

          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>S.No / Action</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Lab Details</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Payment Details</TableCell>
                  <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Address Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paymentData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        No Data Found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paymentData.map((row, index) => (
                    <TableRow key={row.AppId || index} hover>
                      <TableCell sx={{ minWidth: '120px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            #{(currentPage - 1) * 10 + index + 1}
                          </Typography>
                          {row.isapproved === 0 ? (
                            <Tooltip title="Approve Payment">
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleApprove(row.AppId, row.paymenttypeid, row.loginname)}
                                startIcon={<ApproveIcon />}
                                sx={{ fontSize: '0.75rem', px: 1, py: 0.5 }}
                              >
                                Approve
                              </Button>
                            </Tooltip>
                          ) : (
                            <Chip label="Approved" color="success" size="small" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: '300px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            <strong>Lab ID:</strong> {row.ApplicationNo || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                            <strong>Lab Name:</strong> {row.LaboratoryName || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            <strong>Username:</strong> {row.loginname || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            <strong>Type:</strong> {row.LabType || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#ed6c02', fontWeight: 500 }}>
                            <strong>Email:</strong> {row.ContactEmail || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                            <strong>Mobile:</strong> {row.ContactMobile || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: '280px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                            <strong>Mode:</strong> {row.Mode || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                            <strong>UTR/Ref:</strong> {row.Reference_number || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            <strong>Date:</strong> {formatDate(row.Transaction_Date) || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            <strong>Payment Type:</strong> {row.Description || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: '250px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                            <strong>Address:</strong> {row.Address || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                            <strong>City:</strong> {row.City || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            <strong>State:</strong> {row.State || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            <strong>Pincode:</strong> {row.pin || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Bottom Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default OfflinePaymentDetails;